import { ThemeProvider } from '@mui/material/styles';
import { StrictMode, Suspense, useEffect, useMemo } from "react";
import { createRoot, hydrateRoot, type HydrationOptions } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes, useRouteError } from "react-router";
import { RouterProvider } from "react-router/dom";
import RenderMap from "./RenderMap";
import Theme from './Theme';
import { GetAccountData, GetUserPrefs } from "./UserData/_actions/UserDataActions";
import { store } from "./WebsiteStore";
import createEmotionCache from './createEmotionCache';
import { CacheProvider } from '@emotion/react';
import ApplicationDataLayer from './AppData/_components/ApplicationDataLayer';
import { HelmetProvider } from 'react-helmet-async';
import { initReactI18next, useSSR, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import I18NextXhrBackend from 'i18next-xhr-backend';
import i18next, { type Resource } from 'i18next';
import { WebsiteRoutes } from './Website';
import { Settings as DateTimeSettings } from 'luxon';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import PageLoader from './Common/Loader/PageLoader';
import config from '../../config/config.json';
import isAbortError from './Common/Utils/isAbortError';
import captureReactRouteError from './Common/Routing/captureReactRouteError';
import createReactRecoverableErrorHandler from './Common/Routing/createReactRecoverableErrorHandler';
import '@stripe/stripe-js';
import Dinero from 'dinero.js';

window.i18next = i18next;

// Keep Replay from walking third-party iframes, which can throw cross-origin SecurityError on load.
const sentryReplayBlockedSelectors = [
	'iframe[name^="__privateStripe"]',
	'iframe[src*="stripe.com"]',
	'iframe[src*="stripe.network"]',
	'iframe#epik_localstore',
	'iframe[name="epik_localstore"]',
	'iframe[src*="accounts.google.com/gsi/"]',
	'iframe[title="Sign in with Google Button"]',
];

const getHydrationOptions = (reactRoot: string): HydrationOptions | undefined =>
	process.env.NODE_ENV === 'production'
		? { onRecoverableError: createReactRecoverableErrorHandler(reactRoot) }
		: undefined;

const getPreloadedI18nStore = (): Resource | undefined => {
	const store = window.PRELOADED_I18NEXT_STORE;

	if (!store || typeof store !== 'object') {
		return undefined;
	}

	if ('data' in store && store.data && typeof store.data === 'object') {
		return store.data as Resource;
	}

	return store as Resource;
}

const getPreloadedI18nNamespaces = (store: Resource | undefined, locale: string): string[] => {
	const fallbackNamespaces = ['common', 'account'];
	const localeStore = store?.[locale] || store?.[locale.replace('-', '_')] || store?.['en-CA'];
	const namespaces = localeStore ? Object.keys(localeStore) : [];

	return namespaces.length > 0 ? namespaces : fallbackNamespaces;
}

// init sentry
if (['prod','staging','dev'].includes(config.ENVIRONMENT)) {
	Sentry.init({
		dsn: config.sentry.dsn,
		release: config.sentry.release,
		environment: config.ENVIRONMENT,
		integrations: [
			//Sentry.browserTracingIntegration(),
			/*Sentry.httpClientIntegration({
				failedRequestTargets: window.SENTRY_INCLUDE_PATHS,
			}),*/
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
				shouldCreateSpanForRequest: url => !url.startsWith('blob:')
			}),
			Sentry.replayIntegration({
				block: sentryReplayBlockedSelectors,
			}),
			//Sentry.replayCanvasIntegration(),
		],
		tracePropagationTargets: [window.SITE_URL],
		sampleRate: 1,
		tracesSampleRate: config.ENVIRONMENT == "prod" ? 0.3 : 1.0,
		replaysSessionSampleRate: config.ENVIRONMENT == "prod" ? 0.1 : 1.0,
		replaysOnErrorSampleRate: 1.0,
		normalizeDepth: 10,
		beforeSend: (event, hint) => {
			const isPinterestFetchError = event.exception?.values?.some(exception =>
				exception.type === 'TypeError' &&
				(exception.value === 'Failed to fetch' || exception.value === 'Load failed') &&
				exception.stacktrace?.frames?.some(frame =>
					/s\.pinimg\.com\/ct\//i.test(`${frame.filename ?? ''} ${frame.abs_path ?? ''}`)
				)
			) ?? false;
			const hasFailedOpenAIBrowserTelemetry = event.breadcrumbs?.some(breadcrumb => {
				const url: unknown = breadcrumb.data?.url;

				return breadcrumb.category === 'fetch' &&
					breadcrumb.data?.status_code === 0 &&
					typeof url === 'string' &&
					url.startsWith('https://bzr.openai.com/v1/sdk/events');
			}) ?? false;
			const isOpenAIBrowserImageFetchError = hasFailedOpenAIBrowserTelemetry && (
				event.exception?.values?.some(exception =>
					exception.type === 'TypeError' &&
					exception.value === 'Failed to fetch' &&
					exception.stacktrace?.frames?.some(frame => frame.function === 'HTMLImageElement.onload')
				) ?? false
			);

			if (
				isAbortError(hint.originalException) ||
				isAbortError(event.message) ||
				event.exception?.values?.some(exception => exception.type === 'AbortError' || isAbortError(exception.value)) ||
				isPinterestFetchError ||
				isOpenAIBrowserImageFetchError
			) {
				return null;
			}

			return event;
		},
		ignoreErrors: [
			// Random plugins/extensions
		    'top.GLOBALS',
		    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
		    'originalCreateNotification',
		    'canvas.contentDocument',
		    'MyApp_RemoveAllHighlights',
		    'http://tt.epicplay.com',
		    'Can\'t find variable: ZiteReader',
		    'Can\'t find variable: EmptyRanges',
		    'jigsaw is not defined',
		    'ComboSearch is not defined',
		    'http://loading.retry.widdit.com/',
		    'atomicFindClose',
		    'Invalid call to runtime.sendMessage(). Tab not found.',
		    // Facebook borked
		    'fb_xd_fragment',
		    // Android/Facebook in-app browser injected native-bridge noise
		    /Error invoking (enableDidUserTypeOnKeyboardLogging|postMessage): Java object is gone/i,
		    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
		    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
		    'bmi_SafeAddOnload',
		    'EBCallBackMessageReceived',
		    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
		    'conduitPage',
		    'NetworkError when attempting to fetch resource.',
		    'Error in input stream',
		    'Object Not Found Matching Id:',
		    // iOS/Facebook in-app browser injected native-bridge noise
		    /window\.webkit\.messageHandlers/i,
		    // DuckDuckGo's Apple WKWebView wrapper can abandon injected native-bridge replies
		    /WKWebView API client did not respond to this postMessage/i,
		    // Poooli's iOS WKWebView injects a message listener that assumes message data is always a string
		    /msg\.data\.indexOf.*poooliZoomFontSize/i,
		    // PayPal SDK popup/window messaging noise after checkout approval
		    /Can not send postrobot_method\. Target window is closed/i,
		    /Window closed for postrobot_method before response/i
		],
		denyUrls: [
			// Facebook flakiness
		    /graph\.facebook\.com/i,
		    /^iabjs:\/\//i,
		    // Facebook blocked
		    /connect\.facebook\.net\/en_US\/all\.js/i,
		    // Google reCAPTCHA hosted script noise
		    /google\.com\/recaptcha\/api\.js/i,
		    /gstatic\.com\/recaptcha\//i,
		    // Woopra flakiness
		    /eatdifferent\.com\.woopra-ns\.com/i,
		    /static\.woopra\.com\/js\/woopra\.js/i,
		    // Cloudflare analytics can throw when pages are opened from file:// copies
		    /static\.cloudflareinsights\.com\/beacon\.min\.js/i,
		    // Pinterest Tag network noise
		    /s\.pinimg\.com\/ct\/core\.js/i,
		    /ct\.pinterest\.com\//i,
		    // Chrome extensions
		    /extensions\//i,
		    /^chrome:\/\//i,
		    // Safari extensions
		    /safariextension\//i,
		    /PayPal%20Honey\.app/i,
		    // Other plugins
		    /127\.0\.0\.1:4001\/isrunning/i,  // Cacaoweb
		    /webappstoolbarba\.texthelp\.com\//i,
		    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
		]
	});

	Sentry.setUser({
		id: window.APP_USER_ID,
		email: window.APP_USER_EMAIL,
		geo: window.APP_GEO_COUNTRYCODE ? {
			country_code: window.APP_GEO_COUNTRYCODE
		} : undefined,
	});

	Sentry.setTag('logger', 'react.client-app');
	Sentry.setTag("page_locale", window.LOCALE);
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

function LegacyRouteErrorBoundary() {
	const error = useRouteError();
	const location = useLocation();
	const path = location.pathname + location.search + location.hash;

	useEffect(() => {
		if (process.env.NODE_ENV !== 'production') {
			return;
		}

		captureReactRouteError(error, {
			boundary: 'legacy-render-map-route',
			path,
		});
	}, [error, path]);

	if (process.env.NODE_ENV !== 'production') {
		if (error instanceof Error) {
			throw error;
		}

		throw new Error('Unexpected route error');
	}

	return <></>;
}

export default class ReactManager {
	public render = async () => {
		if(!window.PRELOADED_STORE) {
			store.dispatch(GetAccountData());
			store.dispatch(GetUserPrefs());
		}

		//Init translator
		const locale = window.LOCALE.replace('_', '-');
		const currency = store.getState().get('userData').get('prefs').get('currency');
		Dinero.globalLocale = currency === 'USD' ? 'en-US' : locale;
		const preloadedI18nStore = getPreloadedI18nStore();
		const preloadedI18nNamespaces = getPreloadedI18nNamespaces(preloadedI18nStore, locale);

		await i18next
			.use(I18NextXhrBackend)
			.use(initReactI18next)
			.init({
				initAsync: true,
				supportedLngs: ['en-CA','fr-CA'],
				lng: locale,
				fallbackLng: 'en-CA',
				ns: preloadedI18nNamespaces,
				defaultNS: 'common',
				resources: preloadedI18nStore,
				partialBundledLanguages: !!preloadedI18nStore,
				interpolation: {
					escapeValue: false,
				},
				load: 'currentOnly',
				backend: {
					loadPath: window.SITE_URL + 'locale/get/{{lng}}/{{ns}}',
					addPath: window.SITE_URL + 'locale/set/{{lng}}/{{ns}}',
					crossDomain: true,
				},
				keySeparator: '_key_',
				nsSeparator: '_ns_',
				pluralSeparator: '_plural_',
				contextSeparator: '_ctx_',
				saveMissing: false
			}, function (err, t) {
				//Initialized I guess
			});

		DateTimeSettings.defaultLocale = locale

		
		const emotionCache = createEmotionCache();
		let root;

		const reactPageContainer = document.getElementById('main');
		if(reactPageContainer?.className === "react-page") {
			function App() {
				useSSR(preloadedI18nStore || {}, locale)
				const [tR] = useTranslation('routes');
				const router = useMemo(() => sentryCreateBrowserRouter(WebsiteRoutes(tR)), [tR]);

				const strictMode = process.env.NODE_ENV !== 'production';

				const app = <Sentry.ErrorBoundary fallback={<>An error occured</>}>
					<HelmetProvider>
						<Provider store={store}>
							<CacheProvider value={emotionCache}>
								<ThemeProvider theme={Theme}>
									<LocalizationProvider dateAdapter={AdapterLuxon}>
										<Suspense>
											<Suspense fallback={<PageLoader/>}>
												<RouterProvider router={router} />
											</Suspense>
										</Suspense>
									</LocalizationProvider>
								</ThemeProvider>
							</CacheProvider>
						</Provider>
					</HelmetProvider>
				</Sentry.ErrorBoundary>;
				
				return strictMode ? <StrictMode>{ app }</StrictMode> : app;
			} 

			
			if(reactPageContainer.hasChildNodes()) {
				root = hydrateRoot(reactPageContainer, <App/>, getHydrationOptions(reactPageContainer.id))
			} else {
				root = createRoot(reactPageContainer)
				root.render(<App/>)
			}
		}


		//TODO: Legacy RenderMap for all other elements that are non-react pages
		RenderMap.forEach((e) => {
			//Get container
			if(!e.container) {
				return;
			}

			let root;

			function App() {
				useSSR(preloadedI18nStore || {}, locale)
				const strictMode = process.env.NODE_ENV !== 'production';
				const router = useMemo(() => sentryCreateBrowserRouter([
					{ path: "*", Component: e.element, errorElement: <LegacyRouteErrorBoundary /> }
				]), [e.element]);

				const app = <Sentry.ErrorBoundary fallback={<></>}>
					<HelmetProvider>
						<Provider store={store}>
							<CacheProvider value={emotionCache}>
								<ThemeProvider theme={Theme}>
									<LocalizationProvider dateAdapter={AdapterLuxon}>
										<ApplicationDataLayer>
											<Suspense>
												<RouterProvider router={router}/>
											</Suspense>
										</ApplicationDataLayer>
									</LocalizationProvider>
								</ThemeProvider>
							</CacheProvider>
						</Provider>
					</HelmetProvider>
				</Sentry.ErrorBoundary>;
				
				return strictMode ? <StrictMode>{ app }</StrictMode> : app;
			} 

			if(e.container.hasChildNodes()) {
				root = hydrateRoot(e.container, <App/>, getHydrationOptions(e.container.id))
			} else {
				root = createRoot(e.container)
				root.render(<App/>)
			}
		})
	}
}
